import { useCallback, useMemo, useState } from "react";

import { SelectData } from "../../../../models";
import { CompleteTaskRepeatSource } from "../../../../templates/WorkflowTaskPanel";
import {
  GanttTaskViewMode,
  KanbanColumnId,
  KanbanTask,
  KanbanTaskDrop,
  TasksDashboardFilterKey,
  TasksDashboardGroupRow,
  TasksDashboardView,
} from "../../../../widget";
import { OBJECT_TYPE_LABELS } from "../../../../widget/general/TasksDashboard/constants";
import {
  useTasksDashboardAgenda,
  useTasksDashboardExport,
  useTasksDashboardFilterOptions,
  useTasksDashboardGantt,
  useTasksDashboardKanban,
  useTasksDashboardList,
  UseTasksDashboardListReturnData,
  useTasksDashboardNewTask,
  useTasksDashboardObjectLink,
  useTasksDashboardTaskMove,
  useTasksDashboardTaskPanel,
  UseTasksDashboardTaskPanelReturnData,
} from "./hooks";
import { NewTaskParentObject, TasksDashboardLanes, TasksDashboardQueryFilters } from "./models";
import { buildIsRepeatableWorkflowStage, buildWorkflowStageOptions, getWorkflowStageLabel } from "./utils";

export interface UseTasksDashboardArgs {
  /** Selects which view's paging mode is active: per-lane for Kanban, group-then-rows otherwise. */
  view: TasksDashboardView;
  /**
   * Which presentation the Gantt is showing, as the chart reports it. The agenda is one flat query
   * rather than the timeline's group headers, so it decides which of the two the Gantt view issues.
   */
  ganttViewMode?: GanttTaskViewMode;
  /**
   * The one shared task-level filter object, owned by the page. Must be referentially stable
   * (state, or memoised) — a new object every render would refetch everything every render.
   */
  filters?: TasksDashboardQueryFilters;
  pageSize?: number;
}

export interface UseTasksDashboardReturnData {
  /** Org-wide task feed — the Kanban lanes' loaded cards. */
  tasks: KanbanTask[];
  /** Per-lane connections: server `totalCount` for the column headers, plus "show more" paging. */
  lanes: TasksDashboardLanes;
  isLoading: boolean;
  /** Reloads every lane and the group headers from the first page — for use after a mutation. */
  refresh: () => void;
  fetchMoreLane: (columnId: KanbanColumnId) => void;

  /** Tag filter options — the complete list for the filtered set, from the connection's aspects. */
  tagOptions: SelectData;
  /** Options for all four header filters, keyed so their values drive the queries directly. */
  filterOptions: Record<TasksDashboardFilterKey, SelectData>;

  /** Gantt group rows with server counts and lazily loaded rows, plus their loaders. */
  ganttGroups: TasksDashboardGroupRow[];
  onGroupExpand: (objectUuid: string) => void;
  onGroupShowMore: (objectUuid: string) => void;

  /** The Gantt Agenda's flat, deadline-ordered page of the same filtered set, and its pager. */
  agendaTasks: KanbanTask[];
  isLoadingAgendaTasks: boolean;
  hasMoreAgendaTasks: boolean;
  onAgendaShowMore: () => void;

  /** List groups sliced into status sections, plus their loaders. */
  listGroups: UseTasksDashboardListReturnData["listGroups"];
  onSectionExpand: (objectUuid: string, status: string) => void;
  onSectionShowMore: (objectUuid: string, status: string) => void;

  /** True while the group headers for the current view are loading. */
  isLoadingGroups: boolean;
  groupsHaveNextPage: boolean;
  fetchMoreGroups: () => void;

  /**
   * Fired once per drop with the whole dragged block — the drag's `PATCH /workflow/task` per card.
   * Taken as one action rather than per card (`onTaskMove`) because a multi-select drop into Complete
   * collects a single Resolution for the block, which the per-card calls give no boundary for.
   */
  onTaskDrop: (drop: KanbanTaskDrop) => void;
  /** True while the "Complete task" modal is collecting the Resolution for a drop into Complete. */
  showCompleteTaskModal: boolean;
  /** What that modal names as the task being completed — the card's title, or "N tasks". */
  completeTaskLabel: string;
  /**
   * Applies the collected Resolution to the dropped cards. Answers with one repeat source per general
   * task completed, which keeps the modal open on its repeat step for each of them (REQ-008).
   */
  handleSubmitResolution: (resolution: string) => Promise<CompleteTaskRepeatSource[]>;
  /** Cancels a Complete drop — the cards return to the lanes they were dragged from. */
  handleCancelComplete: () => void;
  /** Opens the task in the shared `WorkflowTaskPanel` — every view's task click lands here. */
  onTaskClick: (taskId: string) => void;
  /** Navigates to the card's linked object — KAN-9562. */
  onObjectClick: (taskId: string) => void;
  /** Navigates to a group's parent object (Project / Deal / Landholding) — the grouped views' link. */
  onGroupObjectClick: (objectUuid: string) => void;
  /** Opens the "New task" flow — the type step, then the object chooser (KAN-9565). */
  onNewTask: () => void;
  /** List view "Table options → Export table" — full re-query of the filtered set (KAN-9564 §6). */
  onExportTable: () => void;
  /** True while that export is running. */
  isExporting: boolean;

  /** The "New task" flow's two steps — the type modal, then the parent-object chooser (KAN-9565). */
  showNewTaskTypeModal: boolean;
  canCreateWorkflowTask: boolean;
  canCreateGeneralTask: boolean;
  isLoadingNewTaskTypes: boolean;
  handleNewTaskType: (isGeneralTask: boolean) => void;
  showNewTaskObjectModal: boolean;
  newTaskObjects: NewTaskParentObject[];
  isLoadingNewTaskObjects: boolean;
  newTaskObjectsHaveError: boolean;
  handleNewTaskObjectSearch: (term: string) => void;
  handleNewTaskObject: (object: NewTaskParentObject) => void;
  handleCancelNewTask: () => void;

  /*
    The shared task panel's props, for the page shell to render one `WorkflowTaskPanel`. Merged across
    the two paths that open it — a task click (edit) and the New-task flow (create) — because they are
    the same overlay: two instances would fight over the same `show`, and the panel derives its mode
    from whether a task is present, so the create path is simply the one that supplies none.
  */
  taskPanelTask: UseTasksDashboardTaskPanelReturnData["selectedTask"];
  taskPanelCurrentStepUuid: string | undefined;
  taskPanelWorkflow: UseTasksDashboardTaskPanelReturnData["taskPanelWorkflow"];
  taskPanelIsGeneralTask: boolean;
  taskPanelParentName: string | undefined;
  taskPanelParentObjectType: string | undefined;
  taskPanelParentObjectUuid: string | undefined;
  /*
    The phase/stage picker's options, read off the task's own workflow. Without them the overlay falls
    back to the static `ProjectPhase` enum, which offers a deal workflow project phases that match none
    of its steps — so no step can then be picked, and the create is rejected.
  */
  taskPanelPhaseOptions: SelectData;
  taskPanelPhaseLabel: string;
  taskPanelIsRepeatablePhase: (phase: string | null | undefined) => boolean;
  showTaskPanel: boolean;
  taskPanelRefresh: boolean;
  handleTaskPanelRefresh: () => void;
  handleCloseTaskPanel: () => void;
}

const EMPTY_FILTERS: TasksDashboardQueryFilters = {};

/**
 * Org-wide data for the Tasks dashboard (KAN-9561) — the composition of the three per-view hooks:
 *
 *  - `useTasksDashboardKanban` — one `searchWorkflowTasks` per lane (+ the tag options from the
 *    connection's aspects, which every view's filter bar needs);
 *  - `useTasksDashboardGantt` — group headers, with each group's rows loaded whole on expand;
 *  - `useTasksDashboardList` — the same headers sliced into per-status sections.
 *
 * Every query is built from the same shared filter object with only `status` (and, for a leaf, the
 * group's object) differing (BR-02828), and none of them restricts by `workflowObjectUuid` — the
 * feed is organisation-wide (REQ-002). Only the view on screen issues its group query.
 */
export const useTasksDashboard = ({
  view,
  ganttViewMode = "timeline",
  filters = EMPTY_FILTERS,
  pageSize,
}: UseTasksDashboardArgs): UseTasksDashboardReturnData => {
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback((): void => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  /*
    The lanes load in every view, not just Kanban: they carry the tag filter's options, and they are
    what the flat views fall back to before their own group query has landed.
  */
  const isGanttAgenda = view === "gantt" && ganttViewMode === "agenda";

  const kanban = useTasksDashboardKanban({ filters, pageSize, refreshKey });
  /*
    The Gantt's two presentations read different queries: the timeline wants group headers with rows
    loaded per object, the agenda one flat deadline-ordered list across all of them. Only the one on
    screen loads — the headers still do while the agenda is up, since the group list is what the chart
    falls back to and what the timeline shows the moment the user toggles back.
  */
  const gantt = useTasksDashboardGantt({ filters, enabled: view === "gantt", pageSize, refreshKey });
  const agenda = useTasksDashboardAgenda({ filters, enabled: isGanttAgenda, pageSize, refreshKey });
  const list = useTasksDashboardList({ filters, enabled: view === "list", pageSize, refreshKey });

  const options = useTasksDashboardFilterOptions({ tagOptions: kanban.tagOptions, refreshKey });

  /*
    Export re-queries the whole filtered set from the same shared filter object rather than
    serialising the sections that happen to be expanded (KAN-9564 §6, option B).
  */
  const exportTable = useTasksDashboardExport({ filters });

  // One task-detail surface for every view: the List's task link, and the Kanban / Gantt clicks.
  const taskPanel = useTasksDashboardTaskPanel({ onTaskUpdated: refresh });

  /*
    "New task" (REQ-014). The dashboard is org-wide, so unlike the per-object task tabs it has no parent
    to inherit — the flow asks for the type, then the object, and opens the same overlay in create mode.
  */
  const newTask = useTasksDashboardNewTask();

  /*
    The object link reads the headers of whichever grouped view is on screen: the object's type is only
    known there, and it is what decides between the project, deal and landholding routes.
  */
  const objectLink = useTasksDashboardObjectLink({
    groups: view === "gantt" ? gantt.groups : list.groups,
    tasks: kanban.tasks,
  });

  /*
    Drag-to-restatus. The loaded cards are its source of truth in both directions: they say which lane
    each card is in, and the refresh that follows a move is what commits a success and reverts a
    failure — the board holds the drop optimistically until the reloaded queries tell it otherwise.
  */
  const taskMove = useTasksDashboardTaskMove({ tasks: kanban.tasks, refresh });

  /*
    Whichever path opened the overlay owns its close: an edit has a fetched task and a loaded workflow to
    clear, a create has the chosen type and parent. Both run — only one has anything to reset.
  */
  const { handleCloseTaskPanel: closeEditTaskPanel } = taskPanel;
  const { handleCloseCreateTaskPanel, createTaskParent } = newTask;

  const handleCloseTaskPanel = useCallback((): void => {
    closeEditTaskPanel();
    handleCloseCreateTaskPanel();
  }, [closeEditTaskPanel, handleCloseCreateTaskPanel]);

  /*
    The workflow the overlay is working against, whichever path opened it — the phase picker's options
    are derived from it, so a deal workflow offers its own stages rather than the project phases the
    overlay would otherwise default to.
  */
  const panelWorkflow = taskPanel.selectedTask ? taskPanel.taskPanelWorkflow : newTask.createTaskWorkflow;
  const phaseOptions = useMemo(() => buildWorkflowStageOptions(panelWorkflow), [panelWorkflow]);
  const isRepeatablePhase = useMemo(() => buildIsRepeatableWorkflowStage(panelWorkflow), [panelWorkflow]);

  return {
    tasks: kanban.tasks,
    lanes: kanban.lanes,
    isLoading: kanban.isLoading,
    refresh,
    fetchMoreLane: kanban.fetchMoreLane,

    tagOptions: kanban.tagOptions,
    filterOptions: options.filterOptions,

    ganttGroups: gantt.ganttGroups,
    onGroupExpand: gantt.onGroupExpand,
    onGroupShowMore: gantt.onGroupShowMore,

    agendaTasks: agenda.agendaTasks,
    isLoadingAgendaTasks: agenda.isLoadingAgendaTasks,
    hasMoreAgendaTasks: agenda.hasMoreAgendaTasks,
    onAgendaShowMore: agenda.fetchMoreAgendaTasks,

    listGroups: list.listGroups,
    onSectionExpand: list.onSectionExpand,
    onSectionShowMore: list.onSectionShowMore,

    isLoadingGroups: view === "gantt" ? gantt.isLoadingGroups : list.isLoadingGroups,
    groupsHaveNextPage: view === "gantt" ? gantt.groupsHaveNextPage : list.groupsHaveNextPage,
    fetchMoreGroups: view === "gantt" ? gantt.fetchMoreGroups : list.fetchMoreGroups,

    onTaskDrop: taskMove.onTaskDrop,
    showCompleteTaskModal: taskMove.showCompleteTaskModal,
    completeTaskLabel: taskMove.completeTaskLabel,
    handleSubmitResolution: taskMove.handleSubmitResolution,
    handleCancelComplete: taskMove.handleCancelComplete,
    onObjectClick: objectLink.onObjectClick,

    onNewTask: newTask.onNewTask,
    showNewTaskTypeModal: newTask.showNewTaskTypeModal,
    canCreateWorkflowTask: newTask.canCreateWorkflowTask,
    canCreateGeneralTask: newTask.canCreateGeneralTask,
    isLoadingNewTaskTypes: newTask.isLoadingNewTaskTypes,
    handleNewTaskType: newTask.handleNewTaskType,
    showNewTaskObjectModal: newTask.showNewTaskObjectModal,
    newTaskObjects: newTask.newTaskObjects,
    isLoadingNewTaskObjects: newTask.isLoadingNewTaskObjects,
    newTaskObjectsHaveError: newTask.newTaskObjectsHaveError,
    handleNewTaskObjectSearch: newTask.handleNewTaskObjectSearch,
    handleNewTaskObject: newTask.handleNewTaskObject,
    handleCancelNewTask: newTask.handleCancelNewTask,

    onGroupObjectClick: objectLink.onGroupObjectClick,

    onTaskClick: taskPanel.openTask,
    onExportTable: exportTable.onExportTable,
    isExporting: exportTable.isExporting,

    /*
      Edit supplies the task (and takes its workflow from the task's own board); create supplies none,
      which is what puts the overlay in create mode, plus the type and parent the chooser collected.
      A general task has no workflow, so it passes only the parent — the overlay's `parentObjectType` is
      the stored, capitalised form ("Project"), as on the per-object task tabs.
    */
    taskPanelTask: taskPanel.selectedTask,
    taskPanelCurrentStepUuid: taskPanel.selectedTask?.step?.uuid,
    taskPanelWorkflow: panelWorkflow,
    taskPanelIsGeneralTask: newTask.createTaskIsGeneral,
    taskPanelParentName: taskPanel.selectedTask?.parentObjectName ?? createTaskParent?.name,
    taskPanelParentObjectType: createTaskParent ? OBJECT_TYPE_LABELS[createTaskParent.type] : undefined,
    taskPanelParentObjectUuid: createTaskParent?.uuid,
    taskPanelPhaseOptions: phaseOptions,
    taskPanelPhaseLabel: getWorkflowStageLabel(panelWorkflow),
    taskPanelIsRepeatablePhase: isRepeatablePhase,
    showTaskPanel: taskPanel.showTaskPanel || newTask.showCreateTaskPanel,
    taskPanelRefresh: taskPanel.taskPanelRefresh,
    handleTaskPanelRefresh: taskPanel.handleTaskPanelRefresh,
    handleCloseTaskPanel,
  };
};
